import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { SOSButton } from './SOSButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from './ui/sheet';
import { MobileNavMenu } from './MobileNavMenu';
import { InstallAppBanner } from './InstallAppBanner';
import { User, LogOut, Globe, Menu, Settings, Sparkles, Calendar, BarChart3, Brain, Video, LayoutDashboard } from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group min-w-0" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_stop-tabac-2/artifacts/u4es5x0d_Application.jpeg"
                alt="ZéphyrBreath"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover transition-transform group-hover:scale-105 flex-shrink-0"
              />
              <span className="font-bold text-lg sm:text-xl text-gray-900 tracking-tight truncate">ZéphyrBreath</span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className={`rounded-full px-2 sm:px-3 ${isAuthenticated ? 'hidden sm:inline-flex' : ''}`}
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4 mr-1" />
                {language.toUpperCase()}
              </Button>

              {isAuthenticated ? (
                <>
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" className="sm:hidden rounded-full w-11 h-11 p-0" aria-label="Menu" data-testid="mobile-menu-trigger">
                      <Menu className="w-6 h-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[85vw] max-w-xs bg-white flex flex-col pt-[max(1.5rem,env(safe-area-inset-top))]">
                    <SheetTitle className="px-3 text-left">ZéphyrBreath</SheetTitle>
                    <MobileNavMenu user={user} onLogout={handleLogout} />
                  </SheetContent>
                </Sheet>
                <div className="hidden sm:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full px-3" data-testid="user-menu-trigger">
                      <User className="w-4 h-4 mr-2" />
                      <span>{user?.first_name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center" data-testid="dashboard-link">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        {t('dashboard')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/weekly-tracking" className="flex items-center" data-testid="tracking-link">
                        <Calendar className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Suivi hebdomadaire' : 'Weekly tracking'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/statistics" className="flex items-center" data-testid="statistics-link">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Statistiques' : 'Statistics'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/exercises" className="flex items-center" data-testid="exercises-link">
                        <Brain className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Exercices TCC' : 'CBT exercises'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/videos" className="flex items-center" data-testid="videos-link">
                        <Video className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Vidéos' : 'Videos'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center" data-testid="subscription-link">
                        <Sparkles className="w-4 h-4 mr-2 text-emerald-600" />
                        {language === 'fr' ? 'Abonnement' : 'Subscription'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center" data-testid="settings-link">
                        <Settings className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Paramètres' : 'Settings'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
                </>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="rounded-full h-10 px-3 sm:px-4" data-testid="login-nav-btn">
                      {t('login')}
                    </Button>
                  </Link>
                  <Link to="/register" className="hidden sm:block">
                    <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="register-nav-btn">
                      {t('register')}
                    </Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <InstallAppBanner />
      <main className={isAuthenticated ? 'pb-24 sm:pb-0' : ''}>{children}</main>

      {/* Floating SOS Craving button (only when authenticated) */}
      {isAuthenticated && <SOSButton />}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 mt-auto">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isAuthenticated ? 'pb-28 sm:pb-8' : ''}`}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_stop-tabac-2/artifacts/u4es5x0d_Application.jpeg"
                alt="ZéphyrBreath"
                className="w-6 h-6 rounded object-cover"
              />
              <span className="font-semibold text-gray-900">ZéphyrBreath</span>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} ZéphyrBreath. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
